import { Modal } from "react-bootstrap";
import { navigate } from "@reach/router";
import { useTranslation } from "react-i18next";

import Icon from "Components/Icon/Icon";
import { PLAN_NAMES } from "App/Planes/content";
import COPY from "./copy";
import useAvailablePlans from "./useAvailablePlans";
import styles from "./FeatureBloqueada.module.scss";

/**
 * Estado "esta feature no esta en tu plan" (Figma 3352-22215): un solo componente
 * para todas las features; lo que cambia es el copy (./copy.js).
 *
 * La variante sale del copy y no de un prop, para que no se desincronice del
 * diseno: con `benefits` es la grande (3352:27222), sin `benefits` la compacta
 * (3352:26749). El color sale del plan que se vende, no de la variante.
 *
 * La pill ("Disponible en …") y su color salen de la matriz de `GET /plans`: el plan
 * más barato que trae la feature define el tema. Sólo con el enforcement de planes
 * prendido; apagado, o sin catálogo (cargando, error, o un límite como el techo de
 * agendas), se usa el copy fijo.
 * `inline` es para el tab de ARCA, donde el bloqueo ES la pantalla (3352:27408).
 *
 * Una key sin copy devuelve null: mejor nada que un modal vacio tapando una accion.
 */

function CloseButton({ onClose }) {
  const { t } = useTranslation();
  if (!onClose) return null;
  return (
    <button type="button" className={styles.close} onClick={onClose} aria-label={t("close", "Cerrar")}>
      <Icon name="close_small" size={24} />
    </button>
  );
}

export function FeatureBloqueadaContent({ featureKey, values = {}, onVerPlanes, onClose, inline, secondaryAction }) {
  const { t } = useTranslation();
  const available = useAvailablePlans(featureKey);
  const copy = COPY[featureKey];
  if (!copy) return null;

  const [titleKey, titleDefault] = copy.title;
  const [availabilityKey, availabilityDefault] = copy.availability;
  const planNames = available.map((plan) => {
    const [nameKey, nameDefault] = PLAN_NAMES[plan.code] || [];
    return nameKey ? t(nameKey, nameDefault) : plan.name;
  });
  const availability = planNames.length
    ? t("plan_locked_available_in", "Disponible en {{plans}}", {
        plans: new Intl.ListFormat("es", { type: "conjunction" }).format(planNames),
      })
    : t(availabilityKey, availabilityDefault);
  // Un copy con {{limit}} trae una version sin numero para cuando el tope no llego:
  // mejor eso que "hasta  profesionales".
  const [descKey, descDefault] =
    copy.descriptionWithoutLimit && values.limit == null ? copy.descriptionWithoutLimit : copy.description;
  const cheapest = available[0] ? available[0].code : copy.plan.toLowerCase();
  const theme = cheapest === "premium" ? styles.premium : styles.pro;
  const titleId = `feature-bloqueada-${featureKey}`;

  const title = (
    <h2 id={titleId} className={styles.title}>
      {t(titleKey, titleDefault)}
    </h2>
  );
  const pill = (
    <span className={styles.pill}>
      {availability}
    </span>
  );
  const cta = (
    <button type="button" className={styles.cta} onClick={onVerPlanes}>
      {t("plan_locked_see_plans", "Ver planes")}
    </button>
  );

  // Link opcional debajo del CTA (ej. "Ir al AppStore" desde el menú de cuenta).
  const secondary = secondaryAction?.href && (
    <a
      href={secondaryAction.href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.secondary}
      onClick={() => onClose?.()}
    >
      {secondaryAction.label}
    </a>
  );

  const benefits = copy.benefits && (
    <ul className={styles.benefits}>
      {copy.benefits.map(([key, value]) => (
        <li key={key}>
          <Icon name="check" size={16} className={styles.check} />
          <span>{t(key, value)}</span>
        </li>
      ))}
    </ul>
  );

  // Inline (3352:27408, tab de ARCA): ocupa la pantalla, no interrumpe una accion.
  // Circulo sin candado, sin X, y el pill arriba del CTA (pedido de QA en la card
  // 854, 2026-10-06: el Figma lo tenia debajo).
  if (inline) {
    return (
      <div data-testid="feature-bloqueada" className={`${styles.inlineLayout} ${theme}`}>
        <span data-testid="feature-bloqueada-icon" className={styles.inlineIcon}>
          <Icon name={copy.icon} size={24} />
        </span>
        {title}
        <div className={styles.inlineBody}>
          <p className={styles.description}>{t(descKey, descDefault, values)}</p>
          {benefits}
        </div>
        {pill}
        {cta}
        {secondary}
      </div>
    );
  }

  if (!copy.benefits) {
    return (
      <div data-testid="feature-bloqueada" className={`${styles.compact} ${theme}`}>
        <div className={styles.header}>
          <CloseButton onClose={onClose} />
        </div>
        <div className={styles.hero}>
          <span data-testid="feature-bloqueada-icon" className={styles.iconBadge}>
            <Icon name={copy.icon} size={24} />
            <span data-testid="feature-bloqueada-lock" className={styles.lockBadge}>
              <Icon name="lock" size={12} />
            </span>
          </span>
          {title}
        </div>
        <p className={styles.description}>{t(descKey, descDefault, values)}</p>
        {pill}
        {cta}
        {secondary}
      </div>
    );
  }

  return (
    <div data-testid="feature-bloqueada" className={`${styles.large} ${theme}`}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span data-testid="feature-bloqueada-icon" className={styles.titleIcon}>
            <Icon name={copy.icon} size={16} />
          </span>
          {title}
        </div>
        <CloseButton onClose={onClose} />
      </div>
      <p className={styles.description}>{t(descKey, descDefault, values)}</p>
      {benefits}
      {pill}
      {cta}
      {secondary}
    </div>
  );
}

// El form de turno del "+ Nuevo turno" vive en un offcanvas de Bootstrap (Navbar.js),
// cuya FocusTrap escucha `focusin` en document y devuelve el foco al primer enfocable
// del form ("Bloquear horario") cuando se va afuera. El Modal tiene su propio
// enforceFocus y lo vuelve a traer: loop, y el botón parpadea (QA card 854,
// 2026-10-06). Cortar el focusin en el modal hace que la trampa no lo vea; el
// enforceFocus del Modal no depende de esto (escucha `focus` en captura).
// Efecto lateral: los onFocus de React dentro del modal tampoco llegan (no hay).
const stopFocusinFromReachingDocument = (event) => event.stopPropagation();
const isolateFocus = (node) => node.addEventListener("focusin", stopFocusinFromReachingDocument);
const releaseFocus = (node) => node.removeEventListener("focusin", stopFocusinFromReachingDocument);

export default function FeatureBloqueada({ featureKey, values, show, onClose, onVerPlanes, inline, secondaryAction }) {
  const copy = COPY[featureKey];
  if (!copy) return null;

  // El CTA lleva a la pantalla de Planes (card 1053) y cierra el modal. El consumidor
  // puede pasar `onVerPlanes` para sobreescribirlo.
  const verPlanes =
    onVerPlanes ||
    (() => {
      onClose?.();
      navigate(window._l("/planes"));
    });

  if (inline) {
    return (
      <div className={styles.inline}>
        <FeatureBloqueadaContent featureKey={featureKey} values={values} onVerPlanes={verPlanes} secondaryAction={secondaryAction} inline />
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      onEnter={isolateFocus}
      onExited={releaseFocus}
      centered
      aria-labelledby={`feature-bloqueada-${featureKey}`}
      dialogClassName={copy.benefits ? styles.dialogLarge : styles.dialogCompact}
      contentClassName={styles.modalContent}
    >
      <Modal.Body className="p-4">
        <FeatureBloqueadaContent featureKey={featureKey} values={values} onVerPlanes={verPlanes} onClose={onClose} secondaryAction={secondaryAction} />
      </Modal.Body>
    </Modal>
  );
}
